<template>
  <div class="documentazione-page">
    <div class="card">
      <h2>Documentazione</h2>
      <DocumentationCatalog
        :groups="documentationGroups"
        :loading="loadingDocumentationList"
        :error="documentationListError"
        @refresh="loadDocumentationList"
        @open="openDocumentation"
      />
    </div>
    <Dialog v-model:visible="showDocumentation" :header="documentationTitle" :modal="true"
      :style="{ width: 'min(1100px, 94vw)' }" :maximizable="true">
      <p v-if="loadingDocumentation">Caricamento documento...</p>
      <div v-else-if="documentationError" role="alert">
        <p>{{ documentationError }}</p>
        <Button label="Riprova" icon="pi pi-refresh" @click="openDocumentation(selectedDocumentation)" />
      </div>
      <template v-else>
        <p class="text-color-secondary">Aggiornato il {{ documentationUpdatedAt }}</p>
        <article class="documentation-content" v-html="documentationHtml"></article>
      </template>
    </Dialog>
  </div>
</template>

<script>
import DocumentationCatalog from '@/components/DocumentationCatalog.vue'
import DocumentationService from '@/services/ask-admin/documentation-service'

export default {
  name: 'Documentazione',
  components: { DocumentationCatalog },
  data() {
    return {
      service: null,
      documentationItems: [],
      documentationFailedProjects: [],
      documentationPendingProjects: [],
      loadingDocumentationList: false,
      documentationListError: '',
      showDocumentation: false,
      selectedDocumentation: null,
      documentationTitle: '',
      documentationHtml: '',
      documentationUpdatedAt: '',
      loadingDocumentation: false,
      documentationError: '',
      documentationRequestId: 0,
    }
  },
  created() {
    this.service = new DocumentationService(this.$http, this.$eventBus)
    this.loadDocumentationList()
  },
  beforeUnmount() {
    this.documentationRequestId++
  },
  computed: {
    documentationGroups() {
      return ['ask_pratiche', 'ask_flussi', 'ask_api'].map(project => ({
        project,
        unavailable: this.documentationFailedProjects.includes(project),
        loading: this.documentationPendingProjects.includes(project),
        items: this.documentationItems.filter(document => document.project === project)
          .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')
            || a.title.localeCompare(b.title, 'it') || a.filename.localeCompare(b.filename))
      }))
    },
  },
  methods: {
    async loadDocumentationList() {
      if (this.loadingDocumentationList) return
      this.loadingDocumentationList = true
      this.documentationListError = ''
      this.documentationFailedProjects = []
      this.documentationPendingProjects = ['ask_pratiche', 'ask_flussi', 'ask_api']
      try {
        const response = await this.service.getDocumentationList(result => {
          this.documentationItems = [
            ...this.documentationItems.filter(document => document.project !== result.project),
            ...result.items,
          ]
          if (result.unavailable) this.documentationFailedProjects.push(result.project)
          this.documentationPendingProjects = this.documentationPendingProjects.filter(project => project !== result.project)
          this.documentationListError = this.documentationFailedProjects.length
            ? 'Catalogo non disponibile: ' + this.documentationFailedProjects.join(', ')
            : ''
        })
        this.documentationItems = response.items || []
        this.documentationFailedProjects = response.failed_projects || []
        if (this.documentationFailedProjects.length) this.documentationListError = 'Catalogo non disponibile: ' + this.documentationFailedProjects.join(', ')
      } catch (error) {
        this.documentationFailedProjects = ['ask_pratiche', 'ask_flussi', 'ask_api']
        this.documentationListError = error.response?.data?.message || 'Impossibile caricare la documentazione.'
      } finally {
        this.documentationPendingProjects = []
        this.loadingDocumentationList = false
      }
    },
    async openDocumentation(document) {
      if (!document) return
      const requestId = ++this.documentationRequestId
      this.selectedDocumentation = document
      this.documentationTitle = document.project + ' · ' + document.title
      this.documentationHtml = ''
      this.documentationError = ''
      this.showDocumentation = true
      this.loadingDocumentation = true
      try {
        const response = await this.service.getDocumentation(document.filename, document.project)
        if (requestId !== this.documentationRequestId) return
        this.documentationHtml = response.html || ''
        this.documentationUpdatedAt = response.updated_at || ''
      } catch (error) {
        if (requestId !== this.documentationRequestId) return
        this.documentationError = error.response?.data?.message || 'Impossibile leggere il documento.'
      } finally {
        if (requestId === this.documentationRequestId) this.loadingDocumentation = false
      }
    },
  },
}
</script>

<style scoped>
.documentazione-page { padding: 1rem; }
.documentation-content { line-height: 1.65; overflow-wrap: anywhere; }
.documentation-content :deep(h1), .documentation-content :deep(h2), .documentation-content :deep(h3) { margin-top: 1.4em; line-height: 1.3; }
.documentation-content :deep(pre) { background: #f3f5f7; padding: 1rem; border-radius: 6px; overflow-x: auto; white-space: pre; }
.documentation-content :deep(code) { font-family: Consolas, monospace; }
.documentation-content :deep(table) { border-collapse: collapse; display: block; overflow-x: auto; margin: 1rem 0; }
.documentation-content :deep(th), .documentation-content :deep(td) { border: 1px solid #d5dce5; padding: .5rem .75rem; }
.documentation-content :deep(th) { background: #eef3f8; }
.documentation-content :deep(a) { color: #1677b8; text-decoration: underline; }
</style>
